import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { conditionalConcat } from 'src/helpers/arrayHelpers';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { StudyRoleClaimValue } from 'src/services/api/api-client';
import { DropdownOption } from 'uikit/inputs/dropdown/appDropdownInput';
import {
  ReportScopeEnum,
  StudyReportVariantsType,
  PatientReportVariantsType,
  FileTypeVariantsType,
  FileTypeVariants,
} from './reportExporting';

export const useReportVariantOptions = (exportType: ReportScopeEnum) => {
  const study = useStudy();
  const { t } = useTranslation();
  const { hasStudyClaim } = useHasStudyClaims();

  const studyScopeOptions = useMemo(
    () =>
      conditionalConcat<DropdownOption<StudyReportVariantsType>>(
        hasStudyClaim(StudyRoleClaimValue.ExportAuditLog) && [
          {
            key: 'Auditlog',
            text: t('LogExport.ExportTypes.StudyAuditLogs'),
          },
        ],
        hasStudyClaim(StudyRoleClaimValue.ExportForm) && [
          {
            key: 'Statistics',
            text: t('LogExport.ExportTypes.AllPatientsData'),
          },
        ],
      ),
    [hasStudyClaim, t],
  );

  const patientScopeOptions = useMemo(
    () =>
      conditionalConcat<DropdownOption<PatientReportVariantsType>>(
        hasStudyClaim(StudyRoleClaimValue.ExportAuditLog) && [
          {
            key: 'Auditlog',
            text: t('LogExport.ExportTypes.PatientAuditLogs'),
          },
        ],
        hasStudyClaim(StudyRoleClaimValue.ExportForm) && [
          {
            key: 'Statistics',
            text: t('LogExport.ExportTypes.PatientData'),
          },
        ],
        study?.hasEcrf &&
          hasStudyClaim(StudyRoleClaimValue.ExportPatientEcrfReport) && [
            {
              key: 'eCRF',
              text: t('LogExport.ExportTypes.Ecrf'),
            },
          ],
      ),
    [hasStudyClaim, study?.hasEcrf, t],
  );

  return {
    [ReportScopeEnum.Patient]: patientScopeOptions,
    [ReportScopeEnum.Study]: studyScopeOptions,
  }[exportType];
};

export const useFileTypeOptions = () => {
  const { t } = useTranslation();

  return useMemo<DropdownOption<FileTypeVariantsType>[]>(
    () =>
      FileTypeVariants.map((v) => ({
        key: v,
        text: t(`LogExport.ExportTypes.${v}`, v),
      })),
    [t],
  );
};
